/**
 * WP12e — Puzzle (MgPuzzle): 3×3 / 4×4, drag&snap, zawsze układalne.
 * Nagrody WYŁĄCZNIE przez core/RunClient. Brak game-over / fail-state.
 * UI po polsku z diakrytykami, cele ≥64px.
 */
import Phaser from 'phaser';
import { BaseScene } from '../../core/BaseScene.js';
import { UiKit, MIN_TOUCH } from '../../core/UiKit.js';
import { getSceneRouter } from '../../core/SceneRouter.js';
import { runClient } from '../../core/RunClient.js';
import {
  ROUND_SOFT_MS,
  SNAP_THRESHOLD_RATIO,
  createPuzzle,
  isComplete,
  pieceCount,
  returnSceneForEntry,
  scoreForRun,
  starsForState,
  trySnap,
  type GridSize,
  type PuzzleLayout,
} from './puzzleLogic.js';
import {
  PUZZLE_PICTURES,
  ensurePuzzlePieces,
  ensurePuzzleSource,
  pieceTextureKey,
  type PuzzlePictureId,
} from './puzzleArt.js';

type Phase = 'select' | 'play' | 'done';

type SceneData = {
  entryPoint?: string;
  gameId?: string;
};

type PieceView = {
  id: number;
  container: Phaser.GameObjects.Container;
  homeSlot: number;
  trayX: number;
  trayY: number;
};

export class MgPuzzleScene extends BaseScene {
  static readonly KEY = 'MgPuzzle';

  private phase: Phase = 'select';
  private entryPoint?: string;
  private size: GridSize = 3;
  private pictureId: PuzzlePictureId = 'park';
  private layout: PuzzleLayout | null = null;
  private runId: string | null = null;
  private startedAt = 0;
  private finishing = false;
  private stars = 0;
  private placed = new Map<number, number>(); // pieceId → slot
  private pieces: PieceView[] = [];
  private slotCenters: { x: number; y: number }[] = [];
  private cell = 0;
  private boardOriginX = 0;
  private boardOriginY = 0;
  private hudText: Phaser.GameObjects.Text | null = null;
  private starsText: Phaser.GameObjects.Text | null = null;
  private timerText: Phaser.GameObjects.Text | null = null;
  private hintText: Phaser.GameObjects.Text | null = null;
  private uiLayer: Phaser.GameObjects.GameObject[] = [];
  private dragDepth = 1000;

  constructor() {
    super(MgPuzzleScene.KEY);
  }

  create(data: SceneData = {}): void {
    this.entryPoint = data.entryPoint;
    this.phase = 'select';
    this.runId = null;
    this.finishing = false;
    this.stars = 0;
    this.placed.clear();
    this.pieces = [];
    this.slotCenters = [];
    this.uiLayer = [];
    this.layout = null;

    this.cameras.main.setBackgroundColor(0xe6e0f8);
    this.registry.set('companionFollow', {
      x: this.gameW / 2,
      y: this.gameH - 40,
    });

    this.showSelect();

    // Wyjście w każdej chwili (Esc)
    this.input.keyboard?.on('keydown-ESC', () => this.exitNow());

    this.events.once('shutdown', () => {
      this.input.keyboard?.off('keydown-ESC');
      this.registry.remove('companionFollow');
    });
  }

  update(_t: number, _dt: number): void {
    if (this.phase !== 'play' || !this.timerText || this.finishing) return;
    const elapsed = Date.now() - this.startedAt;
    const sec = Math.floor(elapsed / 1000);
    const softLeft = Math.max(0, Math.ceil((ROUND_SOFT_MS - elapsed) / 1000));
    // Soft timer: po 75 s dalej gramy — brak fail
    if (elapsed < ROUND_SOFT_MS) {
      this.timerText.setText(`Czas: ${sec}s · spokojnie: ${softLeft}s`);
    } else {
      this.timerText.setText(`Czas: ${sec}s · graj dalej ✨`);
    }
  }

  // ─── Wybór rozmiaru ───────────────────────────────────────────────

  private showSelect(): void {
    this.clearUi();
    this.phase = 'select';
    const cx = this.gameW / 2;

    this.track(
      this.add
        .text(cx, 70, 'Puzzle', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '48px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );
    this.track(
      this.add
        .text(cx, 120, 'Wybierz obrazek i układ — zawsze się uda!', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '20px',
          color: '#6b5f7a',
        })
        .setOrigin(0.5),
    );

    // wybór obrazka
    PUZZLE_PICTURES.forEach((pic, i) => {
      const x = cx - 220 + i * 220;
      const selected = this.pictureId === pic.id;
      const btn = UiKit.button(this, {
        x,
        y: 220,
        width: 180,
        height: 90,
        label: `${pic.emoji} ${pic.labelPl}`,
        fill: selected ? 0xffd54f : 0xffffff,
        onClick: () => {
          this.pictureId = pic.id;
          this.showSelect();
        },
      });
      btn.setName(`pic-${pic.id}`);
      this.track(btn);
    });

    // 3×3
    const btn3 = UiKit.button(this, {
      x: cx - 180,
      y: 400,
      width: 220,
      height: 140,
      label: '🧩 3×3',
      fill: 0xc9b1ff,
      onClick: () => void this.startGame(3),
    });
    btn3.setName('size-3');
    this.track(btn3);
    this.track(
      this.add
        .text(cx - 180, 500, 'Łatwe · 9 części', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: '#6b5f7a',
        })
        .setOrigin(0.5),
    );

    // 4×4
    const btn4 = UiKit.button(this, {
      x: cx + 180,
      y: 400,
      width: 220,
      height: 140,
      label: '🧩 4×4',
      fill: 0x90e0ef,
      onClick: () => void this.startGame(4),
    });
    btn4.setName('size-4');
    this.track(btn4);
    this.track(
      this.add
        .text(cx + 180, 500, 'Więcej klocków · 16', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '18px',
          color: '#6b5f7a',
        })
        .setOrigin(0.5),
    );

    this.addExitButton();
  }

  // ─── Start rundy ──────────────────────────────────────────────────

  private async startGame(size: GridSize): Promise<void> {
    if (this.phase === 'play' || this.finishing) return;
    this.size = size;
    this.layout = createPuzzle(size);
    this.placed.clear();
    this.stars = 0;
    this.startedAt = Date.now();
    this.phase = 'play';

    // WAVE7-D: prawdziwy obrazek pocięty na klocki
    try {
      const src = ensurePuzzleSource(this, this.pictureId, 512);
      ensurePuzzlePieces(this, this.pictureId, size, src);
    } catch {
      /* headless / brak canvas */
    }

    try {
      const { runId } = await runClient.startRun('puzzle');
      this.runId = runId;
    } catch {
      // Offline / błąd: gra dalej, bez zł (PLAN §7.5)
      this.runId = null;
    }

    this.showBoard();
  }

  private showBoard(): void {
    this.clearUi();
    if (!this.layout) return;

    const size = this.size;
    const n = pieceCount(size);
    const gap = 8;
    const maxBoard = Math.min(480, this.gameH - 200);
    this.cell = Math.floor((maxBoard - gap * (size - 1)) / size);
    // min touch target
    this.cell = Math.max(this.cell, Math.floor(MIN_TOUCH * 0.9));

    const boardW = size * this.cell + (size - 1) * gap;
    const boardH = boardW;
    this.boardOriginX = 80;
    this.boardOriginY = Math.floor((this.gameH - boardH) / 2);

    const picLabel =
      PUZZLE_PICTURES.find((p) => p.id === this.pictureId)?.labelPl ?? 'Obrazek';
    this.track(
      this.add
        .text(this.gameW / 2, 28, `Ułóż: ${picLabel}`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '32px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );

    this.starsText = this.add
      .text(this.gameW - 40, 28, '⭐ 0', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '28px',
        color: '#4a3f55',
        fontStyle: 'bold',
      })
      .setOrigin(1, 0.5);
    this.track(this.starsText);

    this.timerText = this.add
      .text(40, 28, 'Czas: 0s', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#6b5f7a',
      })
      .setOrigin(0, 0.5);
    this.track(this.timerText);

    this.hintText = this.add
      .text(this.gameW / 2, this.gameH - 36, 'Przeciągnij klocki na siatkę — puszczą same we właściwe miejsce', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#6b5f7a',
      })
      .setOrigin(0.5);
    this.track(this.hintText);

    this.hudText = this.add
      .text(this.gameW / 2, 62, `Ułożone: 0 / ${n}`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '20px',
        color: '#4a3f55',
      })
      .setOrigin(0.5);
    this.track(this.hudText);

    // Ramka planszy
    this.track(
      this.add
        .rectangle(
          this.boardOriginX + boardW / 2,
          this.boardOriginY + boardH / 2,
          boardW + 24,
          boardH + 24,
          0xffffff,
          0.55,
        )
        .setStrokeStyle(4, 0xffffff),
    );

    // Sloty docelowe
    this.slotCenters = [];
    for (let row = 0; row < size; row++) {
      for (let col = 0; col < size; col++) {
        const x = this.boardOriginX + col * (this.cell + gap) + this.cell / 2;
        const y = this.boardOriginY + row * (this.cell + gap) + this.cell / 2;
        this.slotCenters.push({ x, y });
        const ghost = this.add
          .rectangle(x, y, this.cell, this.cell, 0xffffff, 0.28)
          .setStrokeStyle(2, 0xd0c4e8);
        this.track(ghost);
        // delikatny podgląd kafelka z obrazka (ghost)
        const slotIdx = row * size + col;
        const ghostKey = pieceTextureKey(this.pictureId, slotIdx);
        if (this.textures.exists(ghostKey)) {
          this.track(
            this.add
              .image(x, y, ghostKey)
              .setDisplaySize(this.cell - 6, this.cell - 6)
              .setAlpha(0.18),
          );
        }
      }
    }

    // Klocki na tacy (prawa strona)
    this.pieces = [];
    const trayLeft = this.boardOriginX + boardW + 48;
    const trayW = this.gameW - trayLeft - 40;
    const colsTray = size === 3 ? 3 : 4;
    const pieceDraw = Math.min(this.cell, Math.floor((trayW - 8 * (colsTray - 1)) / colsTray));

    this.layout.trayOrder.forEach((id, i) => {
      const tc = i % colsTray;
      const tr = Math.floor(i / colsTray);
      const tx = trayLeft + tc * (pieceDraw + 10) + pieceDraw / 2;
      const ty = this.boardOriginY + tr * (pieceDraw + 10) + pieceDraw / 2;
      const view = this.makePiece(id, tx, ty, pieceDraw);
      this.pieces.push(view);
      this.track(view.container);
    });

    this.addExitButton();
  }

  private makePiece(id: number, x: number, y: number, sizePx: number): PieceView {
    const half = sizePx / 2;
    const key = pieceTextureKey(this.pictureId, id);
    const parts: Phaser.GameObjects.GameObject[] = [];

    // ramka
    const frame = this.add
      .rectangle(0, 0, sizePx, sizePx, 0xffffff, 1)
      .setStrokeStyle(3, 0xffffff)
      .setOrigin(0.5);
    parts.push(frame);

    if (this.textures.exists(key)) {
      const img = this.add
        .image(0, 0, key)
        .setDisplaySize(sizePx - 6, sizePx - 6);
      parts.push(img);
    } else {
      // fallback: pastel bez emoji (gdy brak canvas w testach)
      const fill = 0xbde0fe + ((id * 0x1111) % 0x2222);
      parts.push(
        this.add.rectangle(0, 0, sizePx - 6, sizePx - 6, fill).setOrigin(0.5),
      );
    }

    const c = this.add.container(x, y, parts);
    c.setSize(sizePx, sizePx);
    c.setInteractive(
      new Phaser.Geom.Rectangle(-half, -half, sizePx, sizePx),
      Phaser.Geom.Rectangle.Contains,
    );
    this.input.setDraggable(c);

    const view: PieceView = {
      id,
      container: c,
      homeSlot: id,
      trayX: x,
      trayY: y,
    };

    c.on('dragstart', () => {
      if (this.phase !== 'play' || this.finishing) return;
      if (this.placed.has(id)) return;
      c.setDepth(this.dragDepth++);
      c.setScale(1.06);
    });

    c.on('drag', (_pointer: Phaser.Input.Pointer, dragX: number, dragY: number) => {
      if (this.phase !== 'play' || this.finishing) return;
      if (this.placed.has(id)) return;
      c.x = dragX;
      c.y = dragY;
    });

    c.on('dragend', () => {
      if (this.phase !== 'play' || this.finishing) return;
      if (this.placed.has(id)) return;
      c.setScale(1);
      this.handleDrop(view);
    });

    // Klawiatura: Enter na fokusie nie wymagane — pointer-first

    return view;
  }

  private handleDrop(view: PieceView): void {
    const occupied = new Set(this.placed.values());
    const threshold = this.cell * SNAP_THRESHOLD_RATIO;
    const result = trySnap(
      view.id,
      view.container.x,
      view.container.y,
      this.slotCenters,
      occupied,
      threshold,
    );

    if (result.ok) {
      const center = this.slotCenters[result.slot]!;
      this.tweens.add({
        targets: view.container,
        x: center.x,
        y: center.y,
        duration: 120,
        ease: 'Back.easeOut',
      });
      this.placed.set(view.id, result.slot);
      view.container.disableInteractive();
      this.input.setDraggable(view.container, false);
      this.pulseSlot(center.x, center.y);
      this.refreshHud();
      this.checkWin();
      return;
    }

    // Nie snapa — wróć na tacę (brak kary)
    this.tweens.add({
      targets: view.container,
      x: view.trayX,
      y: view.trayY,
      duration: 180,
      ease: 'Sine.easeOut',
    });
  }

  private pulseSlot(x: number, y: number): void {
    const ring = this.add.circle(x, y, this.cell * 0.4, 0xffffff, 0.5);
    this.tweens.add({
      targets: ring,
      scaleX: 1.4,
      scaleY: 1.4,
      alpha: 0,
      duration: 280,
      onComplete: () => ring.destroy(),
    });
  }

  private refreshHud(): void {
    const n = pieceCount(this.size);
    this.hudText?.setText(`Ułożone: ${this.placed.size} / ${n}`);
    this.stars = starsForState(false, this.stars);
    this.starsText?.setText(`⭐ ${this.stars}`);
  }

  private checkWin(): void {
    if (!isComplete(this.placed, this.size)) return;
    this.stars = starsForState(true, this.stars);
    this.starsText?.setText(`⭐ ${this.stars}`);
    this.hintText?.setText('Brawo! Puzzle ułożone! 🎉');
    void this.finishSuccess();
  }

  private async finishSuccess(): Promise<void> {
    if (this.finishing || this.phase === 'done') return;
    this.finishing = true;
    this.phase = 'done';

    const durationMs = Math.max(0, Date.now() - this.startedAt);
    const stats = {
      stars: this.stars,
      score: scoreForRun(this.placed.size, durationMs, true),
      durationMs,
    };

    let rewardZl = 0;
    if (this.runId) {
      try {
        const res = await runClient.finishRun(this.runId, stats);
        rewardZl = res.rewardZl;
      } catch {
        rewardZl = 0;
      }
    }

    this.spawnConfetti();
    this.time.delayedCall(900, () => {
      getSceneRouter().go('Reward', {
        rewardZl,
        returnTo: returnSceneForEntry(this.entryPoint),
      });
    });
  }

  private spawnConfetti(): void {
    const colors = [0xffb6c1, 0xbde0fe, 0xffe4a0, 0xb8e6d0, 0xc9b1ff];
    for (let i = 0; i < 28; i++) {
      const x = Phaser.Math.Between(80, this.gameW - 80);
      const y = Phaser.Math.Between(40, 200);
      const c = this.add.circle(x, y, Phaser.Math.Between(5, 12), colors[i % colors.length]!);
      this.tweens.add({
        targets: c,
        y: y + Phaser.Math.Between(200, 400),
        alpha: 0,
        duration: Phaser.Math.Between(600, 1200),
        onComplete: () => c.destroy(),
      });
    }
  }

  // ─── Wyjście ──────────────────────────────────────────────────────

  private exitNow(): void {
    if (this.finishing) return;
    // Brak finishRun przy wyjściu — serwer porzuci run przy następnym starcie.
    // NIE liczymy nagrody lokalnie.
    getSceneRouter().go(returnSceneForEntry(this.entryPoint));
  }

  private addExitButton(): void {
    const btn = UiKit.button(this, {
      x: 90,
      y: this.gameH - 48,
      width: 140,
      height: MIN_TOUCH,
      label: 'Wróć',
      fill: 0xffd6e0,
      onClick: () => this.exitNow(),
    });
    btn.setName('btn-exit');
    this.track(btn);
  }

  private track(obj: Phaser.GameObjects.GameObject): void {
    this.uiLayer.push(obj);
  }

  private clearUi(): void {
    for (const o of this.uiLayer) {
      o.destroy();
    }
    this.uiLayer = [];
    this.hudText = null;
    this.starsText = null;
    this.timerText = null;
    this.hintText = null;
    this.pieces = [];
  }
}

export default MgPuzzleScene;
